"use client";
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { FaStar } from "react-icons/fa";
import Image from "next/image";
import styles from "./Testimonials.module.scss";

const testimonials = [
  {
    id: 1,
    name: "Aarav Mehta",
    role: "Entrepreneur",
    company: "Mumbai",
    image:
      "https://images.unsplash.com/photo-1560250097-0b93528c311a?q=80&w=687&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
    quote:
      "Geer made it so easy to find the perfect diamond ring for my fiancée. The quality, service, and packaging were absolutely top-notch!",
    rating: 5,
  },
  {
    id: 2,
    name: "Priya Sharma",
    role: "Fashion Blogger",
    company: "Delhi",
    image:
      "https://images.unsplash.com/photo-1592621385612-4d7129426394?q=80&w=687&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
    quote:
      "I love how elegant and unique Geer's designs are. The customer support was super helpful and the delivery was fast!",
    rating: 5,
  },
  {
    id: 3,
    name: "Ravi Kapoor",
    role: "Banker",
    company: "Bangalore",
    image:
      "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=687&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
    quote:
      "I bought a diamond bracelet for my anniversary from Geer. The craftsmanship is stunning and my wife loved it.",
    rating: 4,
  },
  {
    id: 4,
    name: "Neha Verma",
    role: "Marketing Manager",
    company: "Hyderabad",
    image:
      "https://plus.unsplash.com/premium_photo-1705018501151-4045c97658a3?q=80&w=1170&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
    quote:
      "Shopping on Geer was a great experience. The filters made it easy to choose, and the ring I ordered was even better in person.",
    rating: 5,
  },
];

export default function Testimonials() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);

  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0,
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1,
    },
    exit: (direction) => ({
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0,
    }),
  };

  const swipeConfidenceThreshold = 10000;
  const swipePower = (offset, velocity) => {
    return Math.abs(offset) * velocity;
  };

  const paginate = (newDirection) => {
    setDirection(newDirection);
    setCurrentIndex((prevIndex) => {
      let nextIndex = prevIndex + newDirection;
      if (nextIndex >= testimonials.length) nextIndex = 0;
      if (nextIndex < 0) nextIndex = testimonials.length - 1;
      return nextIndex;
    });
  };

  useEffect(() => {
    const timer = setInterval(() => {
      paginate(1);
    }, 5000);

    return () => clearInterval(timer);
  }, []);

  return (
    <section id="testimonials" className={styles.testimonials}>
      <div className={styles.container}>
        <h2 className={styles.sectionTitle}>
          What Our Customers
          <span className="text-gradient"> Say</span>
        </h2>

        <div className={styles.carousel}>
          <AnimatePresence initial={false} custom={direction}>
            <motion.div
              key={currentIndex}
              className={styles.testimonial}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{
                x: { type: "spring", stiffness: 300, damping: 30 },
                opacity: { duration: 0.2 },
              }}
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={1}
              onDragEnd={(e, { offset, velocity }) => {
                const swipe = swipePower(offset.x, velocity.x);

                if (swipe < -swipeConfidenceThreshold) {
                  paginate(1);
                } else if (swipe > swipeConfidenceThreshold) {
                  paginate(-1);
                }
              }}
            >
              <div className={styles.content}>
                <div className={styles.quote}>
                  {testimonials[currentIndex].quote}
                </div>
                <div className={styles.rating}>
                  {[...Array(testimonials[currentIndex].rating)].map((_, i) => (
                    <FaStar key={i} className={styles.star} />
                  ))}
                </div>
                <div className={styles.author}>
                  <div className={styles.avatar}>
                    <Image
                      src={
                        testimonials[currentIndex].image ||
                        "/image/imageFail.jpg"
                      }
                      alt={testimonials[currentIndex].name}
                      width={100}
                      height={100}
                    />
                  </div>
                  <div className={styles.info}>
                    <h4>{testimonials[currentIndex].name}</h4>
                    <p>{testimonials[currentIndex].role}</p>
                    <p className={styles.company}>
                      {testimonials[currentIndex].company}
                    </p>
                  </div>
                </div>
              </div>
              <button
                aria-label="left button"
                className={`${styles.navButton} ${styles.prev}`}
                onClick={() => paginate(-1)}
              >
                <FiChevronLeft />
              </button>
              <button
                aria-label="right button"
                className={`${styles.navButton} ${styles.next}`}
                onClick={() => paginate(1)}
              >
                <FiChevronRight />
              </button>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
